import { useCallback, useEffect, useState } from "react";
import { Plus, KeyRound, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Skeleton } from "../components/ui/skeleton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

const EMPTY = { name: "", email: "", password: "", role: "technician" };

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/users");
    setUsers(data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/users", form);
      toast.success(`Usuario ${form.email} creado`);
      setCreateOpen(false);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(apiError(err, "No se pudo crear el usuario"));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (u, active) => {
    try {
      await api.put(`/users/${u.id}`, { active });
      toast.success(active ? `${u.name} activado` : `${u.name} desactivado`);
      load();
    } catch (err) {
      toast.error(apiError(err, "No se pudo actualizar el usuario"));
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/users/${resetTarget.id}/reset-password`, { password: newPassword });
      toast.success(`Contraseña de ${resetTarget.name} actualizada`);
      setResetTarget(null);
      setNewPassword("");
    } catch (err) {
      toast.error(apiError(err, "No se pudo restablecer la contraseña"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/users/${deleteTarget.id}`);
      toast.success(`Usuario ${deleteTarget.name} eliminado`);
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(apiError(err, "No se pudo eliminar el usuario"));
    } finally {
      setDeleting(false);
    }
  };

  const openEdit = (u) => {
    setEditForm({
      name: u.name,
      email: u.email,
      role: u.role,
      active: u.active,
      password: "",
    });
    setEditTarget(u);
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/users/${editTarget.id}`, {
        name: editForm.name,
        email: editForm.email,
        role: editForm.role,
        active: editForm.active,
      });
      if (editForm.password) {
        await api.post(`/users/${editTarget.id}/reset-password`, { password: editForm.password });
      }
      toast.success(`Usuario ${editForm.name} actualizado`);
      setEditTarget(null);
      load();
    } catch (err) {
      toast.error(apiError(err, "No se pudo actualizar el usuario"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="users-page">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">
          {users ? `${users.length} usuario${users.length === 1 ? "" : "s"} en el sistema` : "Cargando usuarios..."}
        </p>
        <Button onClick={() => setCreateOpen(true)} className="gap-2 bg-primary hover:bg-[#0353E9]" data-testid="new-user-button">
          <Plus className="h-4 w-4" /> Nuevo Usuario
        </Button>
      </div>

      {users === null ? (
        <div className="space-y-2">
          {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-14 w-full rounded-lg" />)}
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs uppercase tracking-wider">Nombre</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Correo</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Rol</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-right">Órdenes</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Activo</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id} data-testid={`user-row-${u.email}`}>
                    <TableCell className="text-sm font-semibold text-slate-900">
                      {u.name} {u.id === me?.id && <span className="ml-1 text-xs text-slate-400">(usted)</span>}
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">{u.email}</TableCell>
                    <TableCell>
                      <span className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-medium ${
                        u.role === "admin"
                          ? "border-blue-200 bg-blue-50 text-blue-700"
                          : "border-slate-200 bg-slate-50 text-slate-600"
                      }`}>
                        {u.role === "admin" ? "Administrador" : "Técnico"}
                      </span>
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm text-slate-600">{u.order_count}</TableCell>
                    <TableCell>
                      <Switch
                        checked={u.active}
                        disabled={u.id === me?.id}
                        onCheckedChange={(v) => toggleActive(u, v)}
                        data-testid={`user-active-switch-${u.email}`}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost" size="sm" className="gap-1.5 text-slate-500"
                          onClick={() => openEdit(u)}
                          data-testid={`user-edit-${u.email}`}
                        >
                          <Pencil className="h-4 w-4" /> Editar
                        </Button>
                        <Button
                          variant="ghost" size="sm" className="gap-1.5 text-slate-500"
                          onClick={() => setResetTarget(u)}
                          data-testid={`user-reset-${u.email}`}
                        >
                          <KeyRound className="h-4 w-4" /> Contraseña
                        </Button>
                        <Button
                          variant="ghost" size="sm" className="gap-1.5 text-red-500 hover:text-red-600"
                          onClick={() => setDeleteTarget(u)}
                          disabled={u.id === me?.id}
                          data-testid={`user-delete-${u.email}`}
                        >
                          <Trash2 className="h-4 w-4" /> Eliminar
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Nuevo Usuario</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4" data-testid="user-form">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">Nombre completo</Label>
              <Input required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="user-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">Correo electrónico</Label>
              <Input type="email" required value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="user-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">Contraseña temporal</Label>
              <Input type="text" required minLength={6} value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} data-testid="user-password-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">Rol</Label>
              <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
                <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="technician">Técnico</SelectItem>
                  <SelectItem value="admin">Administrador</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving} className="bg-primary hover:bg-[#0353E9]" data-testid="user-form-submit">
                {saving ? "Creando..." : "Crear usuario"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(resetTarget)} onOpenChange={() => setResetTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Restablecer contraseña — {resetTarget?.name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleReset} className="space-y-4" data-testid="reset-password-form">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">Nueva contraseña</Label>
              <Input
                type="text" required minLength={6}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                data-testid="reset-password-input"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setResetTarget(null)}>Cancelar</Button>
              <Button type="submit" disabled={saving} className="bg-primary hover:bg-[#0353E9]" data-testid="reset-password-submit">
                {saving ? "Guardando..." : "Restablecer"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(editTarget)} onOpenChange={() => setEditTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Editar Usuario — {editTarget?.name}</DialogTitle>
          </DialogHeader>
          {editForm && (
            <form onSubmit={handleEdit} className="space-y-4" data-testid="user-edit-form">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">Nombre completo</Label>
                <Input
                  required
                  value={editForm.name}
                  onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                  data-testid="user-edit-name-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">Correo electrónico</Label>
                <Input
                  type="email" required
                  value={editForm.email}
                  onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))}
                  data-testid="user-edit-email-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">Perfil / Rol</Label>
                <Select value={editForm.role} onValueChange={(v) => setEditForm((f) => ({ ...f, role: v }))}>
                  <SelectTrigger data-testid="user-edit-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="technician">Técnico</SelectItem>
                    <SelectItem value="admin">Administrador</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">
                  Nueva contraseña (dejar vacío para mantener la actual)
                </Label>
                <Input
                  type="text" minLength={6}
                  value={editForm.password}
                  onChange={(e) => setEditForm((f) => ({ ...f, password: e.target.value }))}
                  placeholder="Sin cambios"
                  data-testid="user-edit-password-input"
                />
              </div>
              <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2.5">
                <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">Usuario activo</Label>
                <Switch
                  checked={editForm.active}
                  disabled={editTarget?.id === me?.id}
                  onCheckedChange={(v) => setEditForm((f) => ({ ...f, active: v }))}
                  data-testid="user-edit-active-switch"
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setEditTarget(null)} data-testid="user-edit-cancel">
                  Cancelar
                </Button>
                <Button type="submit" disabled={saving} className="bg-primary hover:bg-[#0353E9]" data-testid="user-edit-submit">
                  {saving ? "Guardando..." : "Guardar cambios"}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">¿Eliminar usuario {deleteTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. El usuario ya no podrá iniciar sesión. Sus órdenes existentes conservarán su nombre como técnico asignado.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-user-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="delete-user-confirm"
            >
              {deleting ? "Eliminando..." : "Eliminar usuario"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
